'use client'

import { AcceptedCards } from './AcceptedCards'
import { poweredByRedyUrl } from '@/lib/marketing/poweredBy'
import { PrivacyChoices } from './PrivacyChoices'

type Props = {
  businessName: string
  /** Slug del local: la ruta de la política del merchant se arma COMPLETA con él. */
  slug: string
  address?: string | null
  city?: string | null
  state?: string | null
  phone?: string | null
  email?: string | null
  /** Medición de anuncios activa: muestra «Your Privacy Choices» (exclusión). */
  privacyChoices?: boolean
  /** Política de entrega del local, YA traducida por quien llama. Ausente = el texto en inglés de siempre. */
  fulfillmentPolicy?: { label: string; text: string }
}

function formatFullAddress(address?: string | null, city?: string | null, state?: string | null): string | null {
  const parts = [address, city, state].map((p) => (p ?? '').trim()).filter(Boolean)
  if (parts.length === 0) return null
  return parts.join(', ')
}

function formatPhoneLink(phone?: string | null): { display: string; href: string } | null {
  if (!phone) return null
  const trimmed = phone.trim()
  if (!trimmed) return null
  const digits = trimmed.replace(/\D/g, '')
  const href = digits ? `tel:+${digits.startsWith('1') ? digits : `1${digits}`}` : `tel:${trimmed}`
  return { display: trimmed, href }
}

export function Footer({ businessName, slug, address, city, state, phone, email, privacyChoices = false, fulfillmentPolicy }: Props) {
  const fullAddress = formatFullAddress(address, city, state)
  const phoneInfo = formatPhoneLink(phone)
  const mapsHref = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null

  return (
    <footer className="bg-gray-900 text-white py-12 text-center px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <p className="text-lg font-bold mb-3">{businessName}</p>

          {fullAddress && (
            <p className="mt-2 text-sm text-gray-300">
              {mapsHref ? (
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-white underline-offset-2 hover:underline"
                >
                  <span aria-hidden>📍</span>
                  {fullAddress}
                </a>
              ) : (
                <>📍 {fullAddress}</>
              )}
            </p>
          )}

          {phoneInfo && (
            <p className="mt-2 text-sm text-gray-300">
              <a
                href={phoneInfo.href}
                className="inline-flex items-center gap-1.5 hover:text-white underline-offset-2 hover:underline"
              >
                <span aria-hidden>📞</span>
                {phoneInfo.display}
              </a>
            </p>
          )}

          {email && (
            <p className="mt-2 text-sm text-gray-300">
              <a
                href={`mailto:${email.trim()}`}
                className="inline-flex items-center gap-1.5 hover:text-white underline-offset-2 hover:underline"
              >
                <span aria-hidden>✉️</span>
                {email.trim()}
              </a>
            </p>
          )}
        </div>

        <div className="flex justify-center">
          <AcceptedCards />
        </div>

        <div className="text-xs text-gray-400 space-y-3 max-w-2xl mx-auto leading-relaxed border-t border-gray-800 pt-6 text-left sm:text-center">
          {fulfillmentPolicy ? (
            <p>
              <strong>{fulfillmentPolicy.label}</strong> {fulfillmentPolicy.text}
            </p>
          ) : (
            <p>
              <strong>Fulfillment Policy:</strong> All orders are for in-store pickup or dine-in unless third-party delivery is explicitly specified.
            </p>
          )}
          <p>
            <strong>Transaction Currency:</strong> All prices and transactions are processed in USD.
          </p>
          <p>
            <strong>Security:</strong> Secure payments are processed by North under PCI-DSS compliance via HTTPS/SSL.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-400 mt-6">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline">Privacy Policy</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline">Terms of Service</a>
          {/* Solo con pixel: sin medición de anuncios no se comparte nada. */}
          {privacyChoices && <PrivacyChoices />}
          {/* "Refund Policy" apunta a la política DEL RESTAURANTE: es el enlace
              exacto que revisó underwriting de North y el que hacía falta arreglar.
              La ruta va COMPLETA (`/order/${slug}/...`): bajo dominio propio el
              proxy solo reescribe la raíz, así que una ruta relativa se rompe. */}
          <a href={`/order/${slug}/refund-policy`} target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline">Refund Policy</a>
          {/* La de plataforma se conserva con rótulo distinto para que no se
              confundan: sacarla sería una regresión de divulgación. */}
          <a href="/refund" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline">Redy Platform Policy</a>
          <a href="/merchant-agreement" target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2 hover:underline">Merchant Agreement</a>
        </div>

        <div className="text-gray-600 text-xs mt-8">
          {/* URL absoluta: en el dominio propio del local una ruta relativa
              se quedaría en su sitio. */}
          <a
            href={poweredByRedyUrl({ slug, campaign: 'storefront', placement: 'footer' })}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white underline-offset-2 hover:underline"
          >
            Powered by Redy
          </a>
        </div>
      </div>
    </footer>
  )
}
